/**
 * Workspace-scoped feature flags (`public.feature_flags`).
 * Missing rows are treated as disabled (safe default for rollout).
 */

export const FEATURE_FLAG_KEYS = [
  "dashboard_module",
  "deals_module",
  "pipeline_module",
  "properties_module",
  "rehab_module",
  "financing_module",
  "rentals_module",
  "property_mgmt_module",
  "section8_module",
  "accounting_module",
  "taxes_module",
  "settings_module",
] as const;

export type FeatureFlagKey = (typeof FEATURE_FLAG_KEYS)[number];

export interface FeatureFlagDefinition {
  key: FeatureFlagKey;
  name: string;
  description: string;
}

export const FEATURE_FLAG_DEFINITIONS: FeatureFlagDefinition[] = [
  {
    key: "dashboard_module",
    name: "Dashboard",
    description: "Main dashboard and summary views",
  },
  {
    key: "deals_module",
    name: "Find",
    description: "Lead discovery and deal intake workflows",
  },
  {
    key: "pipeline_module",
    name: "Pipeline",
    description: "Deal pipeline tracking and stage management",
  },
  {
    key: "properties_module",
    name: "Properties",
    description: "Property records and portfolio details",
  },
  {
    key: "rehab_module",
    name: "Rehab",
    description: "Renovation execution and rehab workflows",
  },
  {
    key: "financing_module",
    name: "Financing",
    description: "Lending and financing tools",
  },
  {
    key: "rentals_module",
    name: "Rentals",
    description: "Rental portfolio and lease tracking",
  },
  {
    key: "property_mgmt_module",
    name: "Property management",
    description: "Property management operations",
  },
  {
    key: "section8_module",
    name: "Section 8",
    description: "Housing authority and Section 8 workflows",
  },
  {
    key: "accounting_module",
    name: "Accounting",
    description: "Financial tracking and P&L",
  },
  {
    key: "taxes_module",
    name: "Taxes",
    description: "Tax reporting and Schedule E",
  },
  {
    key: "settings_module",
    name: "Settings",
    description: "Workspace and user settings",
  },
];

export function isKnownFeatureFlagKey(key: string): key is FeatureFlagKey {
  return (FEATURE_FLAG_KEYS as readonly string[]).includes(key);
}

/** True only when a row exists and `enabled` is strictly true. */
export function isFeatureFlagEnabled(
  row: { enabled: boolean | null } | undefined,
): boolean {
  return row !== undefined && row.enabled === true;
}

/** Map app routes (pathname) to a gateable flag. Omitted routes are always shown. */
export const APP_ROUTE_FEATURE_FLAG: Partial<Record<string, FeatureFlagKey>> = {
  "/dashboard": "dashboard_module",
  "/deals": "deals_module",
  "/pipeline": "pipeline_module",
  "/properties": "properties_module",
  "/rehab": "rehab_module",
  "/financing": "financing_module",
  "/rentals": "rentals_module",
  "/property-mgmt": "property_mgmt_module",
  "/section-8": "section8_module",
  "/accounting": "accounting_module",
  "/taxes": "taxes_module",
  "/settings": "settings_module",
};
